import React, { useState, useMemo, useEffect, Fragment } from 'react';
import useVesselsStore from '@/store/vessels';
import { Vessel } from '@/types/api';
import { VoyageItem } from '@/types/voyage';
import { getVesselVoyages } from '@/api/queries/voyages';
import { getDetailVesselReportExport, exportVoyagesReportApi } from '@/api/queries/vessels';
import VoyageDetailModal from './modal-voyage-detail';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Combobox, Transition } from '@headlessui/react';
import BaseDateRangePickerPopOver from '@/components/ui/daterange';
import DatatableWithTheme from '@/components/ui/datatableWIthTheme';
import ShipLoading from '@/components/ui/loader/ShipLoading';
import CheckIcon from '@/assets/icons/check.svg';
import CaretDownIcon from '@/assets/icons/caret-down.svg';
import {
  Route,
  Ship,
  Search,
  FileText,
  FileSpreadsheet,
  Gauge,
  Navigation,
  Clock,
  CheckSquare,
  Square,
  Layers,
  ArrowRight,
  RefreshCw,
  X,
  Calendar,
  Eye,
} from 'lucide-react';
import clsx from 'clsx';

interface ActiveSearchParams {
  vesselId: string;
  vesselName: string;
  status: 'all' | 'completed' | 'ongoing' | 'maneuver' | 'stopped';
  startDate?: string;
  endDate?: string;
  isAllDates: boolean;
}

export default function DetailReportListVoyage() {
  const { vessels } = useVesselsStore();

  // Filter Form States (Belum di-submit ke API)
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const [vesselQuery, setVesselQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'ongoing' | 'maneuver' | 'stopped'>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: moment().subtract(30, 'days').toISOString(),
    end: moment().toISOString(),
  });
  const [isAllDates, setIsAllDates] = useState(false);

  // Active Params: HANYA diisi saat user klik tombol "Tampilkan / Cari"
  // Secara default = null agar TIDAK ADA request data otomatis ke Prediction API di awal load
  const [activeParams, setActiveParams] = useState<ActiveSearchParams | null>(null);

  // Checklist state
  const [selectedVoyages, setSelectedVoyages] = useState<VoyageItem[]>([]);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Detail Modal state
  const [selectedVoyageForDetail, setSelectedVoyageForDetail] = useState<VoyageItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const handleOpenDetail = (voyage: VoyageItem) => {
    setSelectedVoyageForDetail(voyage);
    setShowDetailModal(true);
  };

  // Filtered vessel list for combobox
  const filteredVessels = useMemo(() => {
    if (!vesselQuery) return vessels;
    return vessels.filter((v) =>
      v.vessel_name.toLowerCase().replace(/\s+/g, '').includes(vesselQuery.toLowerCase().replace(/\s+/g, '')),
    );
  }, [vessels, vesselQuery]);

  // Handle Search Trigger (Saat user menekan tombol "Tampilkan")
  const handleSearch = () => {
    if (!selectedVessel) {
      toast.error('Silakan pilih kapal terlebih dahulu');
      return;
    }

    // Gunakan startOf('day') dan endOf('day') dalam format naive ISO YYYY-MM-DDTHH:mm:ss
    // jika filter tanggal aktif, atau kosongkan jika "Lihat Semua (Tanpa Tanggal)" dicentang
    const sDate = !isAllDates && dateRange.start
      ? moment(dateRange.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
      : '';
    const eDate = !isAllDates && dateRange.end
      ? moment(dateRange.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
      : '';

    setActiveParams({
      vesselId: selectedVessel.id,
      vesselName: selectedVessel.vessel_name,
      status: statusFilter,
      startDate: sDate,
      endDate: eDate,
      isAllDates,
    });
  };

  // Fetch voyages from Prediction API
  // enabled: HANYA jika activeParams sudah terisi (user klik tombol cari)
  const {
    data: voyagesResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      'PredictionVoyagesList',
      activeParams?.vesselId,
      activeParams?.status,
      activeParams?.isAllDates,
      activeParams?.startDate,
      activeParams?.endDate,
    ],
    queryFn: () =>
      getVesselVoyages({
        vesselId: activeParams!.vesselId,
        page: 1,
        pageSize: 100, // Ambil semua data pelayaran (maksimal 100) tanpa limit buatan
        status: activeParams!.status,
        startDate: activeParams!.isAllDates ? undefined : (activeParams!.startDate || undefined),
        endDate: activeParams!.isAllDates ? undefined : (activeParams!.endDate || undefined),
        includeRoute: false,
      }),
    enabled: !!activeParams?.vesselId,
  });

  const displayedVoyages = voyagesResponse?.voyages || [];

  // Reset checklist jika kapal atau params berubah
  useEffect(() => {
    setSelectedVoyages([]);
  }, [activeParams?.vesselId, displayedVoyages.length]);

  // Toggle single voyage checklist
  const handleToggleVoyage = (voyage: VoyageItem) => {
    setSelectedVoyages((prev) => {
      const exists = prev.some((v) => v.voyage_key === voyage.voyage_key);
      if (exists) {
        return prev.filter((v) => v.voyage_key !== voyage.voyage_key);
      } else {
        return [...prev, voyage];
      }
    });
  };

  // Toggle select all
  const isAllSelected =
    displayedVoyages.length > 0 && selectedVoyages.length === displayedVoyages.length;
  const isSomeSelected =
    selectedVoyages.length > 0 && selectedVoyages.length < displayedVoyages.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedVoyages([]);
    } else {
      setSelectedVoyages([...displayedVoyages]);
    }
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const listToCalculate = selectedVoyages.length > 0 ? selectedVoyages : displayedVoyages;

    let totalDistKm = 0;
    let totalDistNm = 0;
    let totalDurationHours = 0;
    let speedSum = 0;
    let speedCount = 0;

    listToCalculate.forEach((v) => {
      const dist = v.distance_km || 0;
      totalDistKm += dist;
      totalDistNm += dist * 0.539957;

      const dur = v.travel_time_hours || 0;
      totalDurationHours += dur;

      if (v.avg_speed_knots && v.avg_speed_knots > 0) {
        speedSum += v.avg_speed_knots;
        speedCount++;
      }
    });

    const avgSpeed = speedCount > 0 ? (speedSum / speedCount).toFixed(1) : '0.0';

    return {
      count: listToCalculate.length,
      totalDistNm: totalDistNm.toFixed(1),
      totalDistKm: totalDistKm.toFixed(1),
      avgSpeed,
      totalHours: totalDurationHours.toFixed(1),
      isFiltered: selectedVoyages.length > 0,
    };
  }, [displayedVoyages, selectedVoyages]);

  // Export Handler: Mengambil data telemetri, cuaca, timeline & summary berdasarkan tanggal berangkat dan datang (History Report format)
  const handleExport = async (
    exportType: 'pdf' | 'xlsx',
    scope: 'selected' | 'all' | 'single',
    singleVoyage?: VoyageItem,
  ) => {
    const vesselIdToExport = activeParams?.vesselId || selectedVessel?.id;
    const vesselNameToExport = activeParams?.vesselName || selectedVessel?.vessel_name || 'Vessel';

    if (!vesselIdToExport) {
      toast.error('Silakan pilih kapal terlebih dahulu');
      return;
    }

    let targetVoyages: VoyageItem[] = [];
    if (scope === 'single' && singleVoyage) {
      targetVoyages = [singleVoyage];
    } else if (scope === 'selected') {
      if (selectedVoyages.length === 0) {
        toast.error('Pilih minimal 1 pelayaran melalui checklist');
        return;
      }
      targetVoyages = selectedVoyages;
    } else {
      targetVoyages = displayedVoyages;
    }

    if (targetVoyages.length === 0) {
      toast.error('Tidak ada data pelayaran untuk diekspor. Silakan cari data terlebih dahulu.');
      return;
    }

    // Hitung tanggal berangkat dan tanggal datang
    let fromDate: string;
    let toDate: string;
    let labelSuffix = '';

    if (scope === 'single' && singleVoyage) {
      fromDate = moment(singleVoyage.departure_time).format('YYYY-MM-DD HH:mm:ss');
      toDate = singleVoyage.arrival_time
        ? moment(singleVoyage.arrival_time).format('YYYY-MM-DD HH:mm:ss')
        : moment().format('YYYY-MM-DD HH:mm:ss');
      const origin = (singleVoyage.origin_name || 'Origin').replace(/[^A-Za-z0-9_-]/g, '_');
      const dest = (singleVoyage.destination_name || 'Dest').replace(/[^A-Za-z0-9_-]/g, '_');
      labelSuffix = `${origin}_to_${dest}`;
    } else if (scope === 'selected') {
      const depTimes = targetVoyages
        .map((v) => moment(v.departure_time).valueOf())
        .filter((t) => !isNaN(t));
      const arrTimes = targetVoyages
        .map((v) => (v.arrival_time ? moment(v.arrival_time).valueOf() : moment().valueOf()))
        .filter((t) => !isNaN(t));

      fromDate = moment(Math.min(...depTimes)).format('YYYY-MM-DD HH:mm:ss');
      toDate = moment(Math.max(...arrTimes)).format('YYYY-MM-DD HH:mm:ss');
      labelSuffix = `${targetVoyages.length}_Voyages`;
    } else {
      // scope all
      if (activeParams?.startDate && activeParams?.endDate && !activeParams?.isAllDates) {
        fromDate = moment(activeParams.startDate).format('YYYY-MM-DD HH:mm:ss');
        toDate = moment(activeParams.endDate).format('YYYY-MM-DD HH:mm:ss');
      } else {
        const depTimes = targetVoyages
          .map((v) => moment(v.departure_time).valueOf())
          .filter((t) => !isNaN(t));
        const arrTimes = targetVoyages
          .map((v) => (v.arrival_time ? moment(v.arrival_time).valueOf() : moment().valueOf()))
          .filter((t) => !isNaN(t));

        fromDate =
          depTimes.length > 0
            ? moment(Math.min(...depTimes)).format('YYYY-MM-DD HH:mm:ss')
            : moment().subtract(30, 'days').format('YYYY-MM-DD HH:mm:ss');
        toDate =
          arrTimes.length > 0
            ? moment(Math.max(...arrTimes)).format('YYYY-MM-DD HH:mm:ss')
            : moment().format('YYYY-MM-DD HH:mm:ss');
      }
      labelSuffix = 'All_Voyages';
    }

    try {
      setExporting(true);
      setExportProgress(15);

      const progressInterval = setInterval(() => {
        setExportProgress((prev) => (prev >= 85 ? 85 : prev + 15));
      }, 400);

      // Ekspor menggunakan getDetailVesselReportExport (Format History Report: timeline, cuaca, jarak moving, summary, daily)
      const response = await getDetailVesselReportExport(
        vesselIdToExport,
        fromDate,
        toDate,
        true,
        'history',
        exportType,
        'false',
      );

      clearInterval(progressInterval);
      setExportProgress(100);

      const mime =
        exportType === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const dateStr = moment().format('YYYYMMDD_HHmmss');
      const ext = exportType === 'pdf' ? 'pdf' : 'xlsx';
      const cleanVessel = vesselNameToExport.replace(/[^A-Za-z0-9_-]/g, '_');
      link.download = `History_Report_${cleanVessel}_${labelSuffix}_${dateStr}.${ext}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        `Laporan History ${exportType.toUpperCase()} berhasil diunduh (${fromDate.slice(0, 16)} s/d ${toDate.slice(0, 16)})`,
      );
    } catch (err: any) {
      console.error('Export history report error:', err);
      let errorMessage = `Gagal mengekspor laporan ${exportType.toUpperCase()}. Silakan coba beberapa saat lagi.`;

      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            errorMessage = parsed.message;
          }
        } catch {
          // fallback to default
        }
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 500);
    }
  };

  // Helper formatting
  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      return moment(dateStr).format('DD MMM YYYY, HH:mm');
    } catch {
      return dateStr;
    }
  };

  // Columns definition for Datatable
  const columns = [
    {
      name: (
        <div className="flex items-center gap-1.5 pl-1">
          <button
            type="button"
            onClick={handleToggleSelectAll}
            className="text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400 focus:outline-none transition-colors"
            title={isAllSelected ? 'Deselect All' : 'Select All'}
          >
            {isAllSelected ? (
              <CheckSquare className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            ) : isSomeSelected ? (
              <div className="h-4 w-4 rounded border-2 border-brand-600 dark:border-brand-400 flex items-center justify-center">
                <div className="h-1.5 w-1.5 bg-brand-600 dark:bg-brand-400 rounded-xs" />
              </div>
            ) : (
              <Square className="h-4 w-4" />
            )}
          </button>
        </div>
      ),
      cell: (row: VoyageItem) => {
        const isChecked = selectedVoyages.some((v) => v.voyage_key === row.voyage_key);
        return (
          <div className="py-2 pl-1">
            <button
              type="button"
              onClick={() => handleToggleVoyage(row)}
              className="text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 focus:outline-none transition-colors"
            >
              {isChecked ? (
                <CheckSquare className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              ) : (
                <Square className="h-4 w-4" />
              )}
            </button>
          </div>
        );
      },
      width: '48px',
    },
    {
      name: 'No',
      cell: (_: VoyageItem, index: number) => (
        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{index + 1}</span>
      ),
      width: '50px',
    },
    {
      name: 'Status',
      cell: (row: VoyageItem) => {
        const status = row.status || 'completed';
        const config: Record<string, { label: string; bg: string; text: string; dot: string }> = {
          completed: {
            label: 'Completed',
            bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/50',
            text: 'text-emerald-700 dark:text-emerald-400',
            dot: 'bg-emerald-500',
          },
          ongoing: {
            label: 'Ongoing',
            bg: 'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800/50',
            text: 'text-sky-700 dark:text-sky-400',
            dot: 'bg-sky-500 animate-pulse',
          },
          maneuver: {
            label: 'Maneuver',
            bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/50',
            text: 'text-amber-700 dark:text-amber-400',
            dot: 'bg-amber-500',
          },
          stopped: {
            label: 'Stopped',
            bg: 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
            text: 'text-gray-700 dark:text-gray-300',
            dot: 'bg-gray-400',
          },
        };

        const current = config[status.toLowerCase()] || config.completed;

        return (
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border shadow-2xs',
              current.bg,
              current.text,
            )}
          >
            <span className={clsx('h-1.5 w-1.5 rounded-full', current.dot)} />
            {current.label}
          </span>
        );
      },
      width: '115px',
    },
    {
      name: 'Origin & Destination',
      cell: (row: VoyageItem) => (
        <div
          onClick={() => handleOpenDetail(row)}
          className="py-2.5 space-y-1 min-w-[200px] cursor-pointer group"
          title="Klik untuk melihat detail telemetri & cuaca pelayaran ini"
        >
          <div className="flex items-center gap-1.5 text-xs text-gray-900 dark:text-gray-100 font-medium truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
            <span className="truncate">{row.origin_name || 'Open Water'}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 font-medium truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
            <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
            <span className="truncate">{row.destination_name || 'Open Water'}</span>
          </div>
        </div>
      ),
      minWidth: '220px',
    },
    {
      name: 'Departure & Arrival (UTC)',
      cell: (row: VoyageItem) => (
        <div className="py-2 text-[11px] space-y-0.5 whitespace-nowrap">
          <div className="text-gray-900 dark:text-gray-200 flex items-center gap-1">
            <span className="text-gray-400 text-[10px]">DEP:</span>
            <span>{formatDateTime(row.departure_time)}</span>
          </div>
          <div className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <span className="text-gray-400 text-[10px]">ARR:</span>
            <span>{formatDateTime(row.arrival_time)}</span>
          </div>
        </div>
      ),
      width: '185px',
    },
    {
      name: 'Duration',
      cell: (row: VoyageItem) => {
        const hours = row.travel_time_hours || 0;
        const days = Math.floor(hours / 24);
        const remHours = (hours % 24).toFixed(1);
        const durationText = days > 0 ? `${days}d ${remHours}h` : `${hours.toFixed(1)} hrs`;
        return (
          <div className="py-2 text-xs font-medium text-gray-800 dark:text-gray-200">
            {durationText}
          </div>
        );
      },
      width: '105px',
    },
    {
      name: 'Distance',
      cell: (row: VoyageItem) => {
        const km = row.distance_km || 0;
        const nm = (km * 0.539957).toFixed(1);
        return (
          <div className="py-2">
            <div className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
              {nm} NM
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
              {km.toFixed(1)} km
            </div>
          </div>
        );
      },
      width: '110px',
    },
    {
      name: 'Avg Speed',
      cell: (row: VoyageItem) => (
        <div className="py-2 text-xs font-semibold text-gray-900 dark:text-gray-200 tabular-nums">
          {row.avg_speed_knots ? `${row.avg_speed_knots.toFixed(1)} kn` : '-'}
        </div>
      ),
      width: '100px',
    },
    {
      name: 'Action',
      cell: (row: VoyageItem) => (
        <div className="py-2 flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleOpenDetail(row)}
            className="h-7 w-7 rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 flex items-center justify-center transition-colors shadow-2xs"
            title="Cek Detail Pelayaran (Timeline, Cuaca, Summary, Daily, Chart)"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf', 'single', row)}
            disabled={exporting}
            className="h-7 w-7 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 flex items-center justify-center transition-colors shadow-2xs"
            title="Export History Report (PDF)"
          >
            <FileText className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleExport('xlsx', 'single', row)}
            disabled={exporting}
            className="h-7 w-7 rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 flex items-center justify-center transition-colors shadow-2xs"
            title="Export History Report (Excel)"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
      width: '125px',
      right: true,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Loading Overlay */}
      {exporting && (
        <ShipLoading
          label="Mengekspor laporan ke DigitalOcean Spaces..."
          showProgress={true}
          progress={exportProgress}
          fullScreen={true}
        />
      )}

      {/* ── HEADER BANNER ── */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-gradient-to-r from-brand-600 via-brand-500 to-blue-600 text-white shadow-sm">
        <div
          className="absolute inset-0 opacity-15 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,.45), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.25), transparent 45%)',
          }}
        />
        <div className="relative p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25">
              <Route className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/70 font-semibold mb-0.5">
                Prediction AI Intelligence
              </div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">List Voyage Report</h1>
              <p className="text-xs sm:text-sm text-white/80 mt-0.5 max-w-xl">
                Riwayat pelayaran kapal dari Prediction API, rincian jarak dan durasi layar, serta ekspor PDF & Excel langsung ke DigitalOcean Spaces.
              </p>
            </div>
          </div>

          {/* Quick Header Export Buttons */}
          <div className="flex items-center gap-2 self-stretch sm:self-auto">
            <button
              type="button"
              onClick={() => handleExport('pdf', 'all')}
              disabled={exporting || displayedVoyages.length === 0 || !activeParams}
              className="flex-1 sm:flex-initial h-9 px-3.5 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/25 text-xs font-semibold backdrop-blur inline-flex items-center justify-center gap-1.5 transition-all shadow-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileText className="h-3.5 w-3.5" /> Export All PDF
            </button>
            <button
              type="button"
              onClick={() => handleExport('xlsx', 'all')}
              disabled={exporting || displayedVoyages.length === 0 || !activeParams}
              className="flex-1 sm:flex-initial h-9 px-3.5 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/25 text-xs font-semibold backdrop-blur inline-flex items-center justify-center gap-1.5 transition-all shadow-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" /> Export All Excel
            </button>
          </div>
        </div>
      </div>

      {/* ── FILTER CARD: KAPAL, RENTANG TANGGAL, STATUS & LIMIT (DEFAULT 5 TERAKHIR) ── */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 relative">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500 rounded-l-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end pl-2">
          {/* 1. Filter Kapal */}
          <div className="block lg:col-span-5">
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Pilih Kapal <span className="text-red-500">*</span>
            </span>
            <Combobox value={selectedVessel} onChange={setSelectedVessel}>
              {({ open }) => (
                <div className="relative">
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setVesselQuery('')}
                  >
                    <Combobox.Options
                      className="absolute mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black/5 focus:outline-none sm:text-sm transform translate-y-120 bottom-auto top-full dark:bg-gray-800 dark:text-white border border-gray-100 dark:border-gray-700"
                      style={{ zIndex: 999 }}
                    >
                      {filteredVessels?.length === 0 && vesselQuery !== '' ? (
                        <div className="relative cursor-default select-none px-4 py-2 text-gray-500 text-xs">
                          Kapal tidak ditemukan.
                        </div>
                      ) : (
                        filteredVessels?.map((vessel: Vessel, idx) => (
                          <Combobox.Option
                            key={idx}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-xs ${
                                active
                                  ? 'bg-brand-500 text-white'
                                  : 'text-gray-900 dark:text-gray-200'
                              }`
                            }
                            value={vessel}
                          >
                            {({ selected, active }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                    {vessel?.vessel_name}
                                  </span>
                                  <span className={`text-[10px] ${active ? 'text-white/80' : 'text-gray-400'}`}>
                                    {vessel?.ship_type || 'Ship'}
                                  </span>
                                </div>
                                {selected && (
                                  <span
                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                      active ? 'text-white' : 'text-brand-500'
                                    }`}
                                  >
                                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </Transition>

                  <div className="relative w-full cursor-default overflow-hidden text-left focus:outline-none focus-visible:ring-2 sm:text-sm">
                    <Combobox.Button as="div" className="w-full relative">
                      <Ship className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                      <Combobox.Input
                        className="h-10 w-full pl-9 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/40 relative z-0"
                        displayValue={(v: Vessel) => v?.vessel_name || ''}
                        onChange={(e) => setVesselQuery(e.target.value)}
                        placeholder="Pilih atau cari kapal..."
                        autoComplete="off"
                      />
                    </Combobox.Button>
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <CaretDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </Combobox.Button>
                  </div>
                </div>
              )}
            </Combobox>
          </div>

          {/* 2. Filter Rentang Tanggal & Checklist Semua Tanggal */}
          <div className="block lg:col-span-3">
            <div className="flex items-center justify-between mb-1">
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                Rentang Tanggal
              </span>
              <label className="flex items-center gap-1.5 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={isAllDates}
                  onChange={(e) => setIsAllDates(e.target.checked)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5 cursor-pointer accent-brand-500"
                />
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                  Lihat Semua
                </span>
              </label>
            </div>
            {isAllDates ? (
              <div
                onClick={() => setIsAllDates(false)}
                className="w-full h-10 rounded-lg border border-dashed border-brand-300 dark:border-brand-700/60 bg-brand-50/50 dark:bg-brand-950/20 px-3 flex items-center justify-between text-xs text-brand-700 dark:text-brand-300 cursor-pointer hover:bg-brand-100/50 transition-colors"
                title="Klik untuk memilih rentang tanggal tertentu"
              >
                <span className="flex items-center gap-1.5 font-medium truncate">
                  <Calendar className="h-4 w-4 text-brand-500 shrink-0" />
                  Semua Tanggal (All Time)
                </span>
                <span className="text-[10px] bg-brand-100 dark:bg-brand-900/60 text-brand-700 dark:text-brand-300 px-1.5 py-0.5 rounded-full font-semibold shrink-0">
                  Semua
                </span>
              </div>
            ) : (
              <BaseDateRangePickerPopOver
                value={dateRange}
                setValue={setDateRange}
                className="w-full h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              />
            )}
          </div>

          {/* 3. Filter Status */}
          <div className="block lg:col-span-2">
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Status Pelayaran
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="h-10 w-full px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500/40 cursor-pointer"
            >
              <option value="all">Semua Status</option>
              <option value="completed">Completed</option>
              <option value="ongoing">Ongoing</option>
              <option value="maneuver">Maneuver</option>
              <option value="stopped">Stopped</option>
            </select>
          </div>

          {/* 4. Tombol Tampilkan / Cari */}
          <div className="block lg:col-span-2 flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleSearch}
              disabled={isLoading || isFetching}
              className="h-10 flex-1 px-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors shadow-xs disabled:opacity-50"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span>Tampilkan</span>
            </button>
            {activeParams && (
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isLoading || isFetching}
                className="h-10 w-10 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center justify-center transition-colors shadow-2xs"
                title="Muat Ulang Data"
              >
                <RefreshCw className={clsx('h-4 w-4', isFetching && 'animate-spin')} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── SUMMARY METRIC CARDS ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Total Pelayaran
            </div>
            <div className="h-8 w-8 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <Layers className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {activeParams ? summaryMetrics.count : '-'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">trip</span>
          </div>
          {summaryMetrics.isFiltered ? (
            <span className="inline-block mt-1 text-[10px] text-brand-600 dark:text-brand-400 font-medium">
              ✓ {selectedVoyages.length} dari checklist terpilih
            </span>
          ) : activeParams ? (
            <span className="inline-block mt-1 text-[10px] text-gray-500 dark:text-gray-400">
              Total {displayedVoyages.length} pelayaran ditemukan
            </span>
          ) : (
            <span className="inline-block mt-1 text-[10px] text-gray-400">
              Klik Tampilkan untuk memuat
            </span>
          )}
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Total Jarak
            </div>
            <div className="h-8 w-8 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <Navigation className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {activeParams ? summaryMetrics.totalDistNm : '-'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">NM</span>
          </div>
          <span className="inline-block mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            {activeParams ? `Setara ${summaryMetrics.totalDistKm} km` : 'Belum ada data'}
          </span>
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Rata-rata Speed
            </div>
            <div className="h-8 w-8 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
              <Gauge className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {activeParams ? summaryMetrics.avgSpeed : '-'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">knots</span>
          </div>
          <span className="inline-block mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            Kecepatan rata-rata berlayar
          </span>
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Total Durasi Layar
            </div>
            <div className="h-8 w-8 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {activeParams ? summaryMetrics.totalHours : '-'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">jam</span>
          </div>
          <span className="inline-block mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            Total waktu aktif berlayar
          </span>
        </div>
      </div>

      {/* ── BATCH CHECKLIST ACTION BAR (When checkboxes are checked) ── */}
      {selectedVoyages.length > 0 && (
        <div className="rounded-2xl bg-brand-50 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800/60 p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm transition-all animate-in fade-in">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-brand-500 text-white flex items-center justify-center font-bold text-xs">
              {selectedVoyages.length}
            </div>
            <div>
              <div className="text-xs font-semibold text-brand-900 dark:text-brand-200">
                {selectedVoyages.length} Pelayaran Dipilih via Checklist
              </div>
              <div className="text-[11px] text-brand-700/80 dark:text-brand-300/80">
                Ekspor hanya pelayaran yang dicentang ke format PDF atau Excel.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch sm:self-auto flex-wrap">
            <button
              type="button"
              onClick={() => handleExport('pdf', 'selected')}
              disabled={exporting}
              className="h-8.5 px-3.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 shadow-xs transition-colors"
            >
              <FileText className="h-3.5 w-3.5" /> Export Selected ({selectedVoyages.length}) PDF
            </button>
            <button
              type="button"
              onClick={() => handleExport('xlsx', 'selected')}
              disabled={exporting}
              className="h-8.5 px-3.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 shadow-xs transition-colors"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" /> Export Selected ({selectedVoyages.length}) Excel
            </button>
            <button
              type="button"
              onClick={() => setSelectedVoyages([])}
              className="h-8.5 px-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-1 transition-colors"
              title="Batalkan Pilihan"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── DATATABLE CONTAINER ── */}
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm rounded-2xl p-4 sm:p-5">
        <div className="flex items-center justify-between pb-3 mb-2 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Daftar Riwayat Pelayaran {activeParams ? `(${activeParams.vesselName})` : ''}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Gunakan checklist untuk memilih pelayaran tertentu atau ekspor seluruh {displayedVoyages.length} pelayaran ini.
            </p>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
            Total: {displayedVoyages.length} data
          </div>
        </div>

        <DatatableWithTheme
          columns={columns}
          data={displayedVoyages}
          progressPending={isLoading}
          progressComponent={<ShipLoading label="Memuat Riwayat Pelayaran..." />}
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          pagination
          dense
          noDataComponent={
            <div className="py-14 text-center">
              <Route className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2.5" />
              {!activeParams ? (
                <>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Belum ada data pelayaran yang dimuat
                  </p>
                  <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                    Silakan pilih kapal, rentang tanggal keberangkatan, dan status pelayaran di atas, lalu klik tombol <strong className="text-brand-500">Tampilkan</strong>.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Tidak ada riwayat pelayaran ditemukan
                  </p>
                  <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                    Tidak ditemukan data pelayaran untuk kapal {activeParams.vesselName} pada status {activeParams.status !== 'all' ? `"${activeParams.status}"` : 'yang dipilih'}
                    {!activeParams.isAllDates ? ' dalam rentang tanggal yang dipilih.' : '.'}
                  </p>
                </>
              )}
            </div>
          }
        />
      </div>

      {/* ── DETAIL PREVIEW MODAL (Timeline, Weather, Summary, Daily, Chart) ── */}
      {showDetailModal && selectedVoyageForDetail && (
        <VoyageDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedVoyageForDetail(null);
          }}
          voyage={selectedVoyageForDetail}
          vesselId={selectedVoyageForDetail.vessel_id || activeParams?.vesselId || selectedVessel?.id || ''}
          vesselName={activeParams?.vesselName || selectedVessel?.vessel_name || 'Vessel'}
          onExport={async (type, v) => {
            await handleExport(type, 'single', v);
          }}
          exporting={exporting}
        />
      )}
    </div>
  );
}
